// FileMentionAutocomplete.jsx — dropdown typeahead pra @file:/@folder: (sprint
// composer-anchor E9). Espelha o SlashCommandAutocomplete: keyboard em CAPTURE
// (roda ANTES do onKeyDown do textarea, senão Enter "dava play" na mensagem),
// ↑/↓ navega · Tab/Enter escolhe · Esc fecha. Diretório escolhido mantém '/' e
// re-abre (navegar fundo); arquivo fecha. Reusa file.list via o hook.

import { useEffect, useState } from 'react';
import { File as FileIcon, Folder } from 'lucide-react';
import { useFileMentionSuggestions } from '../../hooks/useFileMentionSuggestions.js';

// v4 de-glass (Cursor IDE parity): flat surface popover — no blur, no specular.
const GLASS_STYLE = `
[data-apolo-filemention]{
  border-radius:10px;
  background:#1c1c1c;
  border:1px solid #2a2a2a;
  box-shadow:0 10px 30px rgba(0,0,0,0.45);
  scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.15) transparent;
}
`;

export default function FileMentionAutocomplete({ input, root, onPick, onDismiss }) {
  const { detected, entries, loading } = useFileMentionSuggestions({ input, root });
  const [activeIdx, setActiveIdx] = useState(0);
  const visible = detected !== null && (entries.length > 0 || loading);

  useEffect(() => {
    setActiveIdx(0);
  }, [entries.length, detected?.partial]);

  useEffect(() => {
    if (!visible) return undefined;
    function onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.min(entries.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        if (entries.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        const entry = entries[activeIdx];
        if (entry) onPick?.(detected, entry);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onDismiss?.();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, entries, activeIdx, detected, onPick, onDismiss]);

  if (!visible) return null;

  return (
    <>
      <style>{GLASS_STYLE}</style>
      <div
        role="listbox"
        aria-label="Arquivos do projeto"
        data-apolo-filemention=""
        className="z-40 max-h-[20rem] w-[min(26rem,92vw)] overflow-y-auto p-1.5"
      >
        {loading && entries.length === 0 && (
          <div className="px-3 py-5 text-center text-[11px] text-white/40">Lendo a pasta…</div>
        )}
        {entries.map((entry, idx) => {
          const isActive = idx === activeIdx;
          const Icon = entry.isDirectory ? Folder : FileIcon;
          return (
            <button
              key={entry.path}
              type="button"
              role="option"
              aria-selected={isActive}
              onClick={() => onPick?.(detected, entry)}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIdx(idx)}
              className={
                'flex w-full items-center gap-2.5 rounded-[12px] px-3 py-2 text-left transition motion-reduce:transition-none ' +
                (isActive ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]')
              }
            >
              <Icon size={14} aria-hidden="true" className={entry.isDirectory ? 'text-apolo-accent' : 'text-white/45'} />
              <span className="truncate font-mono text-[12px] text-white/90">{entry.name}</span>
              {entry.isDirectory && <span className="text-white/30" aria-hidden="true">/</span>}
              {entry.path !== entry.name && (
                <span className="ml-auto truncate text-[10px] text-white/30">{entry.path}</span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
